import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const projects = [
  {
    id: "cashflow",
    title: "Cashflow 101 Tracker",
    description: "A full-featured digital companion for the Cashflow 101 board game. Track income, expenses, investments, passive income, and your path out of the rat race.",
    tags: ["React", "Finance", "Game Tool"],
    path: "/cashflow",
    accent: "#F59E0B",
    year: "2025",
    number: "001",
  },
  {
    id: "monopoly",
    title: "Monopoly Companion",
    description: "A complete digital companion for Monopoly. Animated dice roller, player cash tracking, automatic rent calculation, property management, jail tracker, and Chance & Community Chest cards.",
    tags: ["React", "Board Game", "Game Tool"],
    path: "/monopoly",
    accent: "#22C55E",
    year: "2025",
    number: "002",
  },
];

export default function Portfolio() {
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setTimeout(() => setMounted(true), 50);
  }, []);

  return (
    <div style={{
      minHeight: "100vh",
      background: "#080808",
      color: "#E8E3DC",
      fontFamily: "'Georgia', 'Times New Roman', serif",
      overflowX: "hidden",
      position: "relative",
    }}>
      {/* Subtle grid background */}
      <div style={{
        position: "fixed", inset: 0, pointerEvents: "none", zIndex: 0,
        backgroundImage: `
          linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px),
          linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)
        `,
        backgroundSize: "60px 60px",
      }} />

      {/* Top accent line */}
      <div style={{
        position: "fixed", top: 0, left: 0, right: 0, height: 2,
        background: "linear-gradient(90deg, transparent, #F59E0B, transparent)",
        zIndex: 100,
        opacity: mounted ? 1 : 0,
        transition: "opacity 1s ease 0.2s",
      }} />

      <div style={{ position: "relative", zIndex: 1, maxWidth: 1200, margin: "0 auto", padding: "0 40px" }}>

        {/* Header */}
        <header style={{
          padding: "80px 0 60px",
          borderBottom: "1px solid rgba(255,255,255,0.08)",
          opacity: mounted ? 1 : 0,
          transform: mounted ? "translateY(0)" : "translateY(20px)",
          transition: "all 0.8s ease",
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 24 }}>
            <div>
              <div style={{
                fontSize: 11, letterSpacing: 6, color: "#F59E0B",
                textTransform: "uppercase", marginBottom: 16, fontFamily: "'Georgia', serif"
              }}>
                Projects by
              </div>
              <h1 style={{
                fontSize: "clamp(48px, 8vw, 96px)",
                fontWeight: 400,
                margin: 0,
                lineHeight: 0.9,
                color: "#E8E3DC",
                letterSpacing: "-2px",
              }}>
                Josiah
              </h1>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 12, color: "#4A4540", letterSpacing: 3, textTransform: "uppercase", marginBottom: 8 }}>
                projectsbyjosiah.com
              </div>
              <div style={{ fontSize: 13, color: "#6B6560" }}>
                Building things worth using.
              </div>
            </div>
          </div>
        </header>

        {/* Section label */}
        <div style={{
          padding: "48px 0 32px",
          opacity: mounted ? 1 : 0,
          transition: "opacity 0.8s ease 0.2s",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
            <div style={{ fontSize: 11, letterSpacing: 5, color: "#4A4540", textTransform: "uppercase" }}>
              Selected Work
            </div>
            <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,0.06)" }} />
            <div style={{ fontSize: 11, letterSpacing: 3, color: "#4A4540" }}>
              {projects.length} {projects.length === 1 ? "project" : "projects"}
            </div>
          </div>
        </div>

        {/* Project cards */}
        <div style={{ paddingBottom: 120 }}>
          {projects.map((project, i) => (
            <ProjectCard
              key={project.id}
              project={project}
              index={i}
              mounted={mounted}
              hovered={hovered === project.id}
              onHover={() => setHovered(project.id)}
              onLeave={() => setHovered(null)}
              onClick={() => navigate(project.path)}
            />
          ))}
        </div>

        {/* Footer */}
        <footer style={{
          borderTop: "1px solid rgba(255,255,255,0.06)",
          padding: "40px 0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          opacity: mounted ? 1 : 0,
          transition: "opacity 0.8s ease 0.6s",
        }}>
          <div style={{ fontSize: 12, color: "#3A3530", letterSpacing: 2 }}>
            © {new Date().getFullYear()} Josiah
          </div>
          <div style={{ fontSize: 12, color: "#3A3530", letterSpacing: 2 }}>
            Built with React
          </div>
        </footer>
      </div>
    </div>
  );
}

function ProjectCard({ project, index, mounted, hovered, onHover, onLeave, onClick }) {
  return (
    <div
      onClick={onClick}
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
      style={{
        borderTop: "1px solid rgba(255,255,255,0.08)",
        padding: "48px 0",
        cursor: "pointer",
        opacity: mounted ? 1 : 0,
        transform: mounted ? "translateY(0)" : "translateY(30px)",
        transition: `all 0.7s ease ${0.1 + index * 0.12}s`,
        position: "relative",
      }}
    >
      {/* Hover background sweep */}
      <div style={{
        position: "absolute", inset: "-1px 0",
        background: `linear-gradient(135deg, ${project.accent}06, transparent)`,
        opacity: hovered ? 1 : 0,
        transition: "opacity 0.3s ease",
        pointerEvents: "none",
      }} />

      <div style={{ display: "grid", gridTemplateColumns: "120px 1fr auto", gap: 40, alignItems: "start", position: "relative" }}>
        {/* Number */}
        <div>
          <div style={{
            fontSize: 48, fontWeight: 400, color: hovered ? project.accent : "#2A2520",
            lineHeight: 1, transition: "color 0.3s ease",
            letterSpacing: "-2px",
          }}>
            {project.number}
          </div>
          <div style={{ fontSize: 11, color: "#3A3530", letterSpacing: 3, marginTop: 8 }}>
            {project.year}
          </div>
        </div>

        {/* Content */}
        <div>
          <h2 style={{
            fontSize: "clamp(24px, 4vw, 42px)",
            fontWeight: 400,
            margin: "0 0 16px",
            color: hovered ? "#FFFFFF" : "#E8E3DC",
            letterSpacing: "-0.5px",
            transition: "color 0.3s ease",
            lineHeight: 1.1,
          }}>
            {project.title}
          </h2>
          <p style={{
            fontSize: 15,
            color: "#6B6560",
            margin: "0 0 24px",
            lineHeight: 1.7,
            maxWidth: 520,
          }}>
            {project.description}
          </p>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {project.tags.map(tag => (
              <span key={tag} style={{
                fontSize: 10, letterSpacing: 3, textTransform: "uppercase",
                padding: "5px 12px",
                border: `1px solid ${hovered ? project.accent + "44" : "rgba(255,255,255,0.08)"}`,
                borderRadius: 2,
                color: hovered ? project.accent : "#4A4540",
                transition: "all 0.3s ease",
              }}>
                {tag}
              </span>
            ))}
          </div>
        </div>

        {/* Arrow */}
        <div style={{
          fontSize: 32,
          color: hovered ? project.accent : "#2A2520",
          transform: hovered ? "translate(6px, -6px)" : "translate(0, 0)",
          transition: "all 0.3s ease",
          lineHeight: 1,
          paddingTop: 8,
        }}>
          ↗
        </div>
      </div>
    </div>
  );
}
